import { getFirestore, doc, collection, query, where, getDocs, onSnapshot } from "firebase/firestore"
import getCollectionPath from '@/services/firestore/getCollectionPath'
import { SCHEMA_VERSION, migrateStateDataIfNeeded } from '@/services/migration/legacyMigration'

const db = getFirestore()
const collectionName = 'states/'

/**
 * Variante des lectures ponctuelles : la migration est **attendue** avant de rendre la main,
 * pour que tout code lisant ensuite les domaines les trouve effectivement écrits.
 */
async function docsToInstances(snapshots) {
  const list = []
  const migrations = []
  snapshots.forEach(snapshot => {
    const data = snapshot.data()
    if (!data) return
    migrations.push(migrateStateDataIfNeeded(snapshot.id, data))
    list.push(new State(snapshot.id, data.period, data.team))
  })
  await Promise.all(migrations)
  return list
}

function docToInstance(doc) {
  const data = doc.data()
  if (!data) return null
  // Un document encore au format historique est converti en tâche de fond ; le listener
  // se redéclenchera avec le document allégé une fois la migration commitée.
  migrateStateDataIfNeeded(doc.id, data).catch(error => console.error('Migration de l\'état ' + doc.id + ' impossible', error))
  return new State(doc.id, data.period, data.team)
}

/**
 * Document principal d'un état : identifiants et métadonnées uniquement.
 * Les données métier vivent dans les entités `State*` (voir `stateDomains.js`),
 * chargées et sauvegardées indépendamment.
 */
export default class State {
  constructor(id, period, team) {
    this.id = id
    this.period = period
    this.team = team
    this.schemaVersion = SCHEMA_VERSION
  }

  static initOne(periodId, teamId){
    return new State(null, periodId, teamId)
  }

  toData() {
    return {
      period: this.period,
      team: this.team,
      schemaVersion: SCHEMA_VERSION,
    }
  }

  static async listenAll(callback) {
    return onSnapshot(collection(db, await getCollectionPath(collectionName)), snapshot => {
      const list = []
      snapshot.forEach(doc => {
        list.push(docToInstance(doc))
      })
      callback(list)
    })
  }

  static async listenById(id, callback) {
    return onSnapshot(doc(db, await getCollectionPath(collectionName), id), snapshot => {
      callback(docToInstance(snapshot))
    })
  }

  static async listenByPeriodeAndTeam(periodId, teamId, callback) {
    return onSnapshot(query(collection(db, await getCollectionPath(collectionName)), where("period", "==", periodId), where("team", "==", teamId)), snapshot => {
      const list = []
      snapshot.forEach(doc => {
        list.push(docToInstance(doc))
      })
      callback(list)
    })
  }

  static async listenByTeam(teamId, callback) {
    return onSnapshot(query(collection(db, await getCollectionPath(collectionName)), where("team", "==", teamId)), snapshot => {
      const list = []
      snapshot.forEach(doc => {
        list.push(docToInstance(doc))
      })
      callback(list)
    })
  }

  static async getAll() {
    const response = await getDocs(collection(db, await getCollectionPath(collectionName)))
    return docsToInstances(response)
  }

  static async getByPeriod(periodId){
    const response = await getDocs(query(collection(db, await getCollectionPath(collectionName)), where("period", "==", periodId)))
    return docsToInstances(response)
  }

  static async getByPeriodAndTeam(periodId, teamId){
    const response = await getDocs(query(collection(db, await getCollectionPath(collectionName)), where("period", "==", periodId), where("team", "==", teamId)))
    return docsToInstances(response)
  }

}
